import { authMode, getMockSession } from './auth';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /**
     * The full parsed error body. Some endpoints attach structured detail next to
     * `error` — the fuel km-validation returns per-row findings the grid renders —
     * and dropping it here would force the SPA to re-derive what the API already
     * decided.
     */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function buildAuthHeaders(): Record<string, string> {
  if (authMode() === 'mock') {
    const session = getMockSession();
    return {
      'X-Mock-User-Email': session?.email ?? 'dev@vht.nl',
      'X-Mock-User-Roles': (session?.roles ?? ['admin']).join(','),
    };
  }
  return {}; // entra: the httpOnly session cookie travels via credentials:"include"
}

interface RequestOptions {
  query?: Record<string, string | undefined>;
  body?: unknown;
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined) url.searchParams.set(k, v);
    }
  }
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);

  const res = await fetch(url.toString(), init);
  if (!res.ok) {
    let code = 'API_ERROR';
    let message = `API request failed with status ${res.status}`;
    let body: unknown;
    try {
      body = await res.json();
      const json = body as { error?: { code?: string; message?: string } };
      if (json.error?.code) code = json.error.code;
      if (json.error?.message) message = json.error.message;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiError(res.status, code, message, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * POST raw bytes (no JSON envelope) — the klaarzetlijst photo upload.
 * Metadata rides in headers so the endpoint needs no multipart parser.
 */
export function postBinary<T>(
  path: string,
  bytes: Blob,
  headers: Record<string, string> = {},
): Promise<T> {
  return sendBinary<T>('POST', path, bytes, headers);
}

/** PUT raw bytes — a file that replaces another, like the drawing of a floorplan. */
export function putBinary<T>(
  path: string,
  bytes: Blob,
  headers: Record<string, string> = {},
): Promise<T> {
  return sendBinary<T>('PUT', path, bytes, headers);
}

async function sendBinary<T>(
  method: 'POST' | 'PUT',
  path: string,
  bytes: Blob,
  headers: Record<string, string>,
): Promise<T> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': bytes.type || 'application/octet-stream',
      ...headers,
      ...buildAuthHeaders(),
    },
    body: bytes,
  });
  if (!res.ok) {
    let code = 'API_ERROR';
    let message = `Upload failed with status ${res.status}`;
    let body: unknown;
    try {
      body = await res.json();
      const json = body as { error?: { code?: string; message?: string } };
      if (json.error?.code) code = json.error.code;
      if (json.error?.message) message = json.error.message;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiError(res.status, code, message, body);
  }
  return (await res.json()) as T;
}

/**
 * GET a file as a Blob — for an image the page shows itself (the drawing of a
 * floorplan). An <img src> cannot carry the mock-auth headers; this can, and the
 * session cookie travels along in production as everywhere else.
 */
export async function fetchBlob(path: string): Promise<Blob> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    credentials: 'include',
    headers: buildAuthHeaders(),
  });
  if (!res.ok)
    throw new ApiError(res.status, 'API_ERROR', `Download failed with status ${res.status}`);
  return res.blob();
}

/**
 * GET a file with its headers — a document the server draws again from what it
 * kept (a letter's preview). An error answers JSON, as everywhere.
 */
export async function getForBlob(
  path: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; headers: Headers }> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    credentials: 'include',
    headers: buildAuthHeaders(),
    signal,
  });
  if (!res.ok)
    throw new ApiError(res.status, 'API_ERROR', `Download failed with status ${res.status}`);
  return { blob: await res.blob(), headers: res.headers };
}

/**
 * POST a form and get a file back — a document the server makes from fields and
 * files (the vooraankondiging meerwerk). The headers come along: the server says
 * there how many pages it made. An error answers JSON, as everywhere.
 */
export async function postFormForBlob(
  path: string,
  form: FormData,
  signal?: AbortSignal,
): Promise<{ blob: Blob; headers: Headers }> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    method: 'POST',
    credentials: 'include',
    headers: buildAuthHeaders(), // no Content-Type: the browser sets the multipart boundary
    body: form,
    signal,
  });
  if (!res.ok) {
    let code = 'API_ERROR';
    let message = `Request failed with status ${res.status}`;
    let body: unknown;
    try {
      body = await res.json();
      const json = body as { error?: { code?: string; message?: string } };
      if (json.error?.code) code = json.error.code;
      if (json.error?.message) message = json.error.message;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiError(res.status, code, message, body);
  }
  return { blob: await res.blob(), headers: res.headers };
}

/**
 * POST JSON and get a file back — a document the server draws from what is on the
 * screen (the preview of a quotation). An error answers JSON, as everywhere.
 */
export async function postJsonForBlob(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<{ blob: Blob; headers: Headers }> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let code = 'API_ERROR';
    let message = `Request failed with status ${res.status}`;
    let json: unknown;
    try {
      json = await res.json();
      const e = json as { error?: { code?: string; message?: string } };
      if (e.error?.code) code = e.error.code;
      if (e.error?.message) message = e.error.message;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiError(res.status, code, message, json);
  }
  return { blob: await res.blob(), headers: res.headers };
}

/** POST a form (fields and files) and get JSON back — a letter mailed with its attachment. */
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    method: 'POST',
    credentials: 'include',
    headers: buildAuthHeaders(), // no Content-Type: the browser sets the multipart boundary
    body: form,
  });
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  if (!res.ok) {
    const json = body as { error?: { code?: string; message?: string } } | undefined;
    throw new ApiError(
      res.status,
      json?.error?.code ?? 'API_ERROR',
      json?.error?.message ?? `Request failed with status ${res.status}`,
      body,
    );
  }
  return body as T;
}

/** Hand a Blob the page already has to the browser as a download. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * GET a file and hand it to the browser as a download. Via fetch rather than a
 * plain link, so the mock-auth headers travel along in development too.
 */
export async function downloadBinary(path: string, fileName: string): Promise<void> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    credentials: 'include',
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    let code = 'API_ERROR';
    let message = `Download failed with status ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { code?: string; message?: string } };
      if (json.error?.code) code = json.error.code;
      if (json.error?.message) message = json.error.message;
    } catch {
      // non-JSON error body — keep defaults
    }
    throw new ApiError(res.status, code, message);
  }
  saveBlob(await res.blob(), fileName);
}

export const apiClient = {
  get<T>(path: string, query?: Record<string, string | undefined>): Promise<T> {
    return request<T>('GET', path, { query });
  },
  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>('POST', path, { body });
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>('PUT', path, { body });
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>('PATCH', path, { body });
  },
  delete<T>(path: string): Promise<T> {
    return request<T>('DELETE', path);
  },
};
